/**
 * RecordatoriosCompartidos.js
 * Página pública para ver y descargar recordatorios compartidos como .ics
 * Accesible sin autenticación via /compartido/recordatorios/:token
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../services/api/config';

/**
 * Genera contenido ICS para uno o más recordatorios
 */
function generarICS(recordatorios) {
  const escaparTexto = (texto) => {
    if (!texto) return '';
    return texto
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  const formatICSDateLocal = (fechaStr) => {
    // fechaStr format: "YYYY-MM-DD"
    // Generate as local time in America/Mexico_City (09:00)
    const parts = fechaStr.split('-');
    return `${parts[0]}${parts[1]}${parts[2]}T090000`;
  };

  const formatICSDateLocalEnd = (fechaStr) => {
    const parts = fechaStr.split('-');
    return `${parts[0]}${parts[1]}${parts[2]}T100000`;
  };

  const formatICSTimestamp = () => {
    return new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const eventos = recordatorios.map((rec) => {
    const fecha = rec.fechaUnica || rec.fechaPrimeraOcurrencia;
    if (!fecha) return '';

    const uid = `rec-${rec.recordatorioId}-${Date.now()}@lemmontags.com`;
    const titulo = rec.nombrePersonalizado || 'Recordatorio';

    // Build description
    let descripcion = '';
    if (rec.monto != null) {
      descripcion += `$${parseFloat(rec.monto).toLocaleString('es-MX')} ${rec.moneda || 'MXN'}`;
    }
    if (rec.notas) {
      if (descripcion) descripcion += ' - ';
      descripcion += rec.notas;
    }
    if (rec.documentoNombre) {
      if (descripcion) descripcion += ' - ';
      descripcion += rec.documentoNombre;
    }

    const lineas = [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${formatICSTimestamp()}`,
      `DTSTART;TZID=America/Mexico_City:${formatICSDateLocal(fecha)}`,
      `DTEND;TZID=America/Mexico_City:${formatICSDateLocalEnd(fecha)}`,
      `SUMMARY:${escaparTexto(titulo)}`,
      `DESCRIPTION:${escaparTexto(descripcion)}`,
      'BEGIN:VALARM',
      'TRIGGER:-P1D',
      'ACTION:DISPLAY',
      'DESCRIPTION:Recordatorio de pago mañana',
      'END:VALARM',
      'END:VEVENT'
    ];

    return lineas.join('\r\n');
  }).filter(Boolean);

  const calendario = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LemmonTags//Recordatorios//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    // Timezone definition
    'BEGIN:VTIMEZONE',
    'TZID:America/Mexico_City',
    'BEGIN:STANDARD',
    'DTSTART:19701025T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
    'TZOFFSETFROM:-0500',
    'TZOFFSETTO:-0600',
    'TZNAME:CST',
    'END:STANDARD',
    'BEGIN:DAYLIGHT',
    'DTSTART:19700405T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=4;BYDAY=1SU',
    'TZOFFSETFROM:-0600',
    'TZOFFSETTO:-0500',
    'TZNAME:CDT',
    'END:DAYLIGHT',
    'END:VTIMEZONE',
    ...eventos,
    'END:VCALENDAR'
  ];

  return calendario.join('\r\n');
}

function descargarArchivo(contenido, nombre) {
  const blob = new Blob([contenido], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function RecordatoriosCompartidos() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Token no proporcionado');
      setLoading(false);
      return;
    }

    fetchRecordatorios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchRecordatorios = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recordatorios-compartidos/publico/${token}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 410 || errorData.revocado) {
          setError('revocado');
        } else if (response.status === 404) {
          setError('no_encontrado');
        } else {
          setError('error_general');
        }
        setLoading(false);
        return;
      }

      const result = await response.json();
      setData(result);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recordatorios compartidos:', err);
      setError('error_conexion');
      setLoading(false);
    }
  };

  const handleDescargarIndividual = (rec) => {
    const ics = generarICS([rec]);
    const nombre = `${(rec.nombrePersonalizado || 'recordatorio').replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, '_')}.ics`;
    descargarArchivo(ics, nombre);
  };

  const handleDescargarTodos = () => {
    if (!data?.recordatorios) return;
    const ics = generarICS(data.recordatorios);
    descargarArchivo(ics, 'recordatorios-lemmontags.ics');
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    try {
      return new Date(fechaStr + 'T12:00:00').toLocaleDateString('es-MX', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch {
      return fechaStr;
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center">
          <div className="spinner-border mb-3" style={{ color: '#84cc16' }} role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Cargando recordatorios...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="text-center" style={{ maxWidth: '400px', padding: '0 20px' }}>
          <div style={{ marginBottom: '24px' }}>
            <img
              src="/lemmon_logo.png"
              alt="LemmonTags"
              style={{ height: '32px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          {error === 'revocado' ? (
            <>
              <i className="bi bi-link-45deg text-warning" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Este link ya no está disponible</h5>
              <p className="text-muted">El propietario revocó el acceso a estos recordatorios.</p>
            </>
          ) : error === 'no_encontrado' ? (
            <>
              <i className="bi bi-question-circle text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Link no encontrado</h5>
              <p className="text-muted">Este link no existe o ya no está disponible.</p>
            </>
          ) : (
            <>
              <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">Error al cargar</h5>
              <p className="text-muted">No se pudieron cargar los recordatorios. Intenta de nuevo más tarde.</p>
            </>
          )}
          <a href="https://lemmontags.com" className="btn btn-outline-secondary btn-sm mt-3">
            Ir a LemmonTags
          </a>
        </div>
      </div>
    );
  }

  // Success
  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div className="text-center py-4" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        <img
          src="/lemmon_logo.png"
          alt="LemmonTags"
          style={{ height: '28px', marginBottom: '8px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <h5 style={{ color: '#374151', marginBottom: '4px' }}>Recordatorios compartidos</h5>
        {data?.objetoNombre && (
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{data.objetoNombre}</p>
        )}
      </div>

      {/* Recordatorios list */}
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '20px 16px' }}>
        {data?.recordatorios?.map((rec) => {
          const fecha = rec.fechaUnica || rec.fechaPrimeraOcurrencia;
          return (
            <div
              key={rec.recordatorioId}
              style={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span style={{ fontSize: '20px' }}>📅</span>
                    <strong style={{ color: '#374151', fontSize: '0.95rem' }}>
                      {rec.nombrePersonalizado || 'Recordatorio'}
                    </strong>
                  </div>
                  <div style={{ marginLeft: '28px', fontSize: '0.85rem', color: '#6b7280' }}>
                    {fecha && <span>{formatFecha(fecha)}</span>}
                    {rec.monto != null && (
                      <>
                        <span style={{ margin: '0 6px' }}>&middot;</span>
                        <span>${parseFloat(rec.monto).toLocaleString('es-MX')} {rec.moneda || 'MXN'}</span>
                      </>
                    )}
                  </div>
                  {rec.notas && (
                    <div style={{ marginLeft: '28px', fontSize: '0.8rem', color: '#9ca3af', marginTop: '4px' }}>
                      {rec.notas}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm"
                  onClick={() => handleDescargarIndividual(rec)}
                  style={{
                    backgroundColor: '#84cc16',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '6px 12px',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-download me-1"></i>
                  .ics
                </button>
              </div>
            </div>
          );
        })}

        {/* Download all button */}
        {data?.recordatorios?.length > 1 && (
          <button
            className="btn w-100 mt-2"
            onClick={handleDescargarTodos}
            style={{
              backgroundColor: '#84cc16',
              color: '#fff',
              border: 'none',
              padding: '14px',
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            <i className="bi bi-download me-2"></i>
            Descargar todos (.ics)
          </button>
        )}

        {/* Footer info */}
        <div className="text-center mt-4">
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>
            Los archivos .ics son compatibles con Google Calendar, Apple Calendar y Outlook.
          </p>
          <a
            href="https://lemmontags.com"
            className="text-decoration-none"
            style={{ fontSize: '0.8rem', color: '#84cc16' }}
          >
            Conoce LemmonTags
          </a>
        </div>
      </div>
    </div>
  );
}
